// WHAT: Service for analyzing trends in Withings measurement data
// WHY:  Provides trend calculation and direction determination for health metrics
// DOMAIN: Health → Withings
// DEPENDENCIES: WithingsMeasurement type
// RISKS:
//   - NOTE: Trend calculation requires at least 2 measurements - may return null frequently
//   - NOTE: Neutral threshold values are hardcoded - may not match all metrics
//   - NOTE: Good direction is binary - may not account for context
//   - NOTE: Date filtering may have timezone issues

import type { WithingsMeasurement } from "../types/database";

/**
 * WHAT:
 *   Trend analysis result structure.
 *
 * WHY:
 *   Provides trend information with visual indicators.
 */
export interface TrendResult {
  /** Color indicator */
  color: "green" | "red" | "yellow";
  /** Trend description */
  description: string;
  /** Trend direction */
  direction: "down" | "neutral" | "up";
  /** Trend label */
  label: string;
  /** Percentage change value */
  value: number;
}

/**
 * WHAT:
 *   Configuration for trend analysis of a specific metric.
 *
 * WHY:
 *   Defines how to analyze trends for each metric type.
 */
export interface TrendConfig {
  /** Direction that is considered good */
  goodDirection: "down" | "up";
  /** Display label */
  label: string;
  /** Metric key from measurement */
  metric: keyof WithingsMeasurement;
  /** Threshold for neutral trend */
  neutralThreshold: number;
  /** Timeframe definitions in days */
  timeframes: {
    "7d": number;
    "30d": number;
    "1y": number;
  };
  /** Unit of measurement */
  unit: string;
}

/**
 * WHAT:
 *   Configuration for all Withings metrics trend analysis.
 *
 * WHY:
 *   Provides centralized configuration for trend calculations.
 *
 * NOTE: Threshold values are hardcoded - may not match all use cases
 */
const TREND_CONFIGS: Record<string, TrendConfig> = {
  bmi: {
    goodDirection: "down",
    label: "BMI",
    metric: "bmi",
    neutralThreshold: 0.2,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "",
  },
  bodyFat: {
    goodDirection: "down",
    label: "Body Fat",
    metric: "body_fat_percent",
    neutralThreshold: 0.5,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "%",
  },
  bodyWater: {
    goodDirection: "up",
    label: "Body Water",
    metric: "body_water_percent",
    neutralThreshold: 0.5,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "%",
  },
  boneMass: {
    goodDirection: "up",
    label: "Bone Mass",
    metric: "bone_mass_kg",
    neutralThreshold: 0.1,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "kg",
  },
  fatFreeMass: {
    goodDirection: "up",
    label: "Fat Free Mass",
    metric: "fat_free_mass_kg",
    neutralThreshold: 0.3,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "kg",
  },
  fatFreeMassPercent: {
    goodDirection: "up",
    label: "Fat Free Mass (%)",
    metric: "fat_free_mass_percent",
    neutralThreshold: 0.5,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "%",
  },
  fatMass: {
    goodDirection: "down",
    label: "Fat Mass",
    metric: "fat_mass_kg",
    neutralThreshold: 0.3,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "kg",
  },
  muscleMass: {
    goodDirection: "up",
    label: "Muscle Mass",
    metric: "muscle_mass_kg",
    neutralThreshold: 0.3,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "kg",
  },
  visceralFat: {
    goodDirection: "down",
    label: "Visceral Fat",
    metric: "visceral_fat",
    neutralThreshold: 0.2,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "",
  },
  weight: {
    goodDirection: "down",
    label: "Weight",
    metric: "weight_kg",
    neutralThreshold: 0.5,
    timeframes: { "1y": 365, "7d": 7, "30d": 30 },
    unit: "kg",
  },
};

/**
 * WHAT:
 *   Service class for analyzing trends in Withings measurement data.
 *
 * WHY:
 *   Provides trend calculation and direction determination for health metrics
 *   across different timeframes.
 *
 * BEHAVIOR:
 *   - Calculates trends for individual metrics
 *   - Calculates trends for all metrics
 *   - Determines trend direction and color coding
 *   - Filters measurements by timeframe
 *
 * RISKS:
 *   - NOTE: Trend calculation requires at least 2 measurements - may return null frequently
 *   - NOTE: Neutral threshold values are hardcoded - may not match all metrics
 *   - NOTE: Good direction is binary - may not account for context
 *   - NOTE: Date filtering may have timezone issues
 */
export const TrendAnalysisService = {
  /**
   * WHAT:
   *   Calculates trend for a specific metric and timeframe.
   *
   * WHY:
   *   Provides trend analysis for individual metrics.
   *
   * BEHAVIOR:
   *   - Filters measurements by timeframe
   *   - Compares latest vs oldest value
   *   - Calculates percentage change
   *   - Determines direction and color based on good direction
   *
   * @param measurements - Array of measurements
   *
   * @param metricKey - Metric key identifier
   *
   * @param timeframe - Time period for trend calculation
   *
   * @returns {null | TrendResult} TrendResult if sufficient data available, null otherwise
   *
   */
  calculateTrend(
    measurements: WithingsMeasurement[],
    metricKey: string,
    timeframe: "1y" | "7d" | "30d",
  ): null | TrendResult {
    const config = TREND_CONFIGS[metricKey];
    if (!config) {
      // NOTE: No trend config found for metric
      return null;
    }

    const days = config.timeframes[timeframe];
    // NOTE: Filter measurements by timeframe
    const filtered = measurements.filter((m: WithingsMeasurement) => {
      const measurementDate = new Date(m.measurement_date);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      return measurementDate >= cutoffDate;
    });

    // Reason: toSorted() returns a new array, but TypeScript may infer unsafe type

    const recentMeasurements: WithingsMeasurement[] = [...filtered].toSorted(
      (a: WithingsMeasurement, b: WithingsMeasurement) =>
        new Date(b.measurement_date).getTime() - new Date(a.measurement_date).getTime(),
    );

    if (recentMeasurements.length < 2) {
      return null;
    }

    const latest = recentMeasurements[0];
    const oldest = recentMeasurements.at(-1);

    if (!oldest) {
      return null;
    }

    if (!latest) {
      return null;
    }
    const latestValueRaw: unknown = latest[config.metric];
    const oldestValueRaw: unknown = oldest[config.metric];

    if (typeof latestValueRaw !== "number" || typeof oldestValueRaw !== "number") {
      return null;
    }

    const latestValue = latestValueRaw;
    const oldestValue = oldestValueRaw;

    const change = latestValue - oldestValue;
    const changePercent = (change / oldestValue) * 100;

    // NOTE: Determine direction and color
    let direction: "down" | "neutral" | "up";
    let color: "green" | "red" | "yellow";
    let label: string;
    let description: string;

    if (Math.abs(change) <= config.neutralThreshold) {
      direction = "neutral";
      color = "yellow";
      label = "Stable";
      description = `${config.label} is stable`;
    } else if (change > 0) {
      direction = "up";
      if (config.goodDirection === "up") {
        color = "green";
        label = "Improvement";
        description = `${config.label} is increasing`;
      } else {
        color = "red";
        label = "Decline";
        description = `${config.label} is increasing`;
      }
    } else {
      direction = "down";
      if (config.goodDirection === "down") {
        color = "green";
        label = "Improvement";
        description = `${config.label} is decreasing`;
      } else {
        color = "red";
        label = "Decline";
        description = `${config.label} is decreasing`;
      }
    }

    return {
      color,
      description,
      direction,
      label,
      value: changePercent,
    };
  },

  /**
   * WHAT:
   *   Calculates trends for all configured metrics.
   *
   * WHY:
   *   Provides comprehensive trend analysis across all health metrics.
   *
   * BEHAVIOR:
   *   - Iterates through all trend configs
   *   - Calculates trend for each metric
   *   - Returns map of metric keys to trend results
   *
   * @param measurements - Array of measurements
   *
   * @param timeframe - Time period for trend calculation
   *
   * @returns {Record<string, null | TrendResult>} Map of metric keys to trend results
   *
   */
  calculateAllTrends(
    measurements: WithingsMeasurement[],
    timeframe: "1y" | "7d" | "30d",
  ): Record<string, null | TrendResult> {
    const trends: Record<string, null | TrendResult> = {};

    for (const metricKey of Object.keys(TREND_CONFIGS)) {
      trends[metricKey] = this.calculateTrend(measurements, metricKey, timeframe);
    }

    return trends;
  },

  /**
   * Gibt alle verfügbaren Metriken zurück
   *
   * @returns {string[]} Array of available metric keys
   *
   */
  getAvailableMetrics(): string[] {
    return Object.keys(TREND_CONFIGS);
  },

  /**
   * Gibt die Konfiguration für eine Metrik zurück
   *
   * @param metricKey - Metric key identifier
   *
   * @returns {null | TrendConfig} Metric configuration or null if not found
   *
   */
  getMetricConfig(metricKey: string): null | TrendConfig {
    return TREND_CONFIGS[metricKey] ?? null;
  },
};
